import { Injectable, NgZone, inject } from '@angular/core';
import {
  Firestore,
  collection,
  collectionData,
  doc,
  limit,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
} from '@angular/fire/firestore';
import { Observable, combineLatest, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import {
  PLATFORM_NOTIFICATION_EVENTS,
  PLATFORM_NOTIFICATIONS_SUBCOLLECTION,
  PLATFORM_USERS_COLLECTION,
  PlatformNotification,
  PlatformNotificationEventType,
  PlatformNotificationSent,
  RECENT_SENT_LIMIT,
  SubscriberIdentity,
  platformNotificationPath,
} from './platform-notification.model';

const SENT_COLLECTION = 'sent';

/** Firestore snapshot callbacks can fire outside the Angular zone; re-enter it so the panel repaints. */
function runInZone<T>(zone: NgZone) {
  return (source: Observable<T>): Observable<T> =>
    new Observable<T>((observer) =>
      source.subscribe({
        next: (v) => zone.run(() => observer.next(v)),
        error: (e) => zone.run(() => observer.error(e)),
        complete: () => zone.run(() => observer.complete()),
      }),
    );
}

function toMillis(value: any): number {
  if (!value) return 0;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (typeof value.seconds === 'number') return value.seconds * 1000;
  const d = new Date(value);
  return isNaN(d.getTime()) ? 0 : d.getTime();
}

/**
 * Per-super-admin platform notification settings.
 * Path: platform_users/{uid}/notifications/{eventType}
 *
 * Reads stream from Firestore; writes are plain single-document merges (a
 * subscription row has no side effects, so no callable — see the Data Access
 * Conventions in CLAUDE.md). Delivery happens in blazebite-functions, which
 * queries this collection when a platform event fires and logs what it sent
 * under the matching row's `sent` subcollection.
 */
@Injectable({ providedIn: 'root' })
export class PlatformNotificationsService {
  private firestore = inject(Firestore);
  private zone = inject(NgZone);

  /** All of one user's rows, one per event they have ever toggled. */
  getRows(uid: string): Observable<PlatformNotification[]> {
    return (collectionData(this.collectionRef(uid), { idField: 'id' }) as Observable<PlatformNotification[]>).pipe(
      runInZone(this.zone),
    );
  }

  /**
   * The user's most recent alerts across every catalog event, newest first.
   * One `sent` listener per event (each ordered by createdAt, capped), merged
   * and re-capped client-side.
   */
  getRecentSent(uid: string): Observable<PlatformNotificationSent[]> {
    const streams = PLATFORM_NOTIFICATION_EVENTS.map((event) => {
      const sentRef = collection(this.docRef(uid, event.eventType), SENT_COLLECTION);
      const q = query(sentRef, orderBy('createdAt', 'desc'), limit(RECENT_SENT_LIMIT));
      return collectionData(q, { idField: 'id' }) as Observable<PlatformNotificationSent[]>;
    });
    if (streams.length === 0) return of([]);
    return combineLatest(streams).pipe(
      map((lists) =>
        lists
          .flat()
          .sort((a, b) => toMillis(b.createdAt) - toMillis(a.createdAt))
          .slice(0, RECENT_SENT_LIMIT),
      ),
      runInZone(this.zone),
    );
  }

  /**
   * Turn an event on or off for the user. Refreshes the delivery address and
   * display name from the identity on every save so the row never goes stale.
   */
  setEventEnabled(
    identity: SubscriberIdentity,
    eventType: PlatformNotificationEventType,
    enabled: boolean,
    create: boolean,
  ): Promise<void> {
    const data: Record<string, unknown> = {
      uid: identity.uid,
      eventType,
      enabled,
      email: identity.email,
      displayName: identity.displayName ?? null,
      updatedAt: serverTimestamp(),
    };
    if (create) {
      data['channel'] = 'email';
      data['createdAt'] = serverTimestamp();
    }
    return setDoc(this.docRef(identity.uid, eventType), data, { merge: true });
  }

  private collectionRef(uid: string) {
    return collection(this.firestore, PLATFORM_USERS_COLLECTION, uid, PLATFORM_NOTIFICATIONS_SUBCOLLECTION);
  }

  private docRef(uid: string, eventType: PlatformNotificationEventType) {
    return doc(this.firestore, ...platformNotificationPath(uid, eventType));
  }
}

/** Convenience for callers that only have an event type and want to know if it's supported. */
export function isKnownPlatformEvent(eventType: string): eventType is PlatformNotificationEventType {
  return PLATFORM_NOTIFICATION_EVENTS.some((e) => e.eventType === eventType);
}
